import { usePlacesStore } from "../stores/placesStore";
import { useUrlFilters } from "../lib/useUrlFilters";
import {
  NORMALIZED_CATEGORIES,
  CATEGORY_LABELS,
  OUTREACH_STATUS_LABELS,
} from "@hsh/shared";
import type { OutreachStatus, NormalizedCategory } from "@hsh/shared";

interface FilterPanelProps {
  onCreatePlan: () => void;
  isMobile?: boolean;
}

export default function FilterPanel({
  onCreatePlan,
  isMobile = false,
}: FilterPanelProps) {
  const { pagination } = usePlacesStore();
  const {
    dataFilters: filters,
    setFilters,
    clearFilters,
    activeFilterCount,
  } = useUrlFilters();

  return (
    <div
      className={
        isMobile
          ? ""
          : "bg-white rounded-lg shadow-lg w-72 flex flex-col max-h-[calc(100vh-96px)]"
      }
    >
      {!isMobile && (
        <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-semibold text-gray-900">
            Filters{" "}
            {activeFilterCount > 0 && (
              <span className="ml-1 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </h2>
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Reset
          </button>
        </div>
      )}

      {isMobile && (
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-gray-500">
            {activeFilterCount > 0
              ? `${activeFilterCount} filters active`
              : "No filters active"}
          </span>
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Reset All Filters
          </button>
        </div>
      )}

      {/* Scrollable filter content */}
      <div
        className={`${isMobile ? "" : "overflow-y-auto flex-1 p-4"} space-y-${
          isMobile ? "6" : "4"
        }`}
      >
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            value={filters.search || ""}
            onChange={(e) =>
              setFilters({ search: e.target.value || undefined })
            }
            placeholder="Business name..."
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              isMobile ? "py-3 text-base" : ""
            }`}
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={filters.category || ""}
            onChange={(e) =>
              setFilters({
                category: (e.target.value as NormalizedCategory) || undefined,
              })
            }
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              isMobile ? "py-3 text-base" : ""
            }`}
          >
            <option value="">All Categories</option>
            {NORMALIZED_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_LABELS[cat as NormalizedCategory]}
              </option>
            ))}
          </select>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street / Postcode / Area
          </label>
          <input
            type="text"
            value={filters.location || ""}
            onChange={(e) =>
              setFilters({ location: e.target.value || undefined })
            }
            placeholder="e.g., High Street, DH1, Durham"
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              isMobile ? "py-3 text-base" : ""
            }`}
          />
        </div>

        {/* Score Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Score Range: {filters.minScore || 0} - {filters.maxScore || 100}
          </label>
          <div className="flex items-center gap-2 w-full overflow-hidden">
            <input
              type="range"
              min="0"
              max="100"
              value={filters.minScore || 0}
              onChange={(e) =>
                setFilters({ minScore: parseInt(e.target.value) || undefined })
              }
              className={`flex-1 min-w-0 ${isMobile ? "h-2" : ""}`}
            />
            <input
              type="range"
              min="0"
              max="100"
              value={filters.maxScore || 100}
              onChange={(e) =>
                setFilters({ maxScore: parseInt(e.target.value) || undefined })
              }
              className={`flex-1 min-w-0 ${isMobile ? "h-2" : ""}`}
            />
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Outreach Status
          </label>
          <select
            value={filters.status || ""}
            onChange={(e) =>
              setFilters({
                status: (e.target.value as OutreachStatus) || undefined,
              })
            }
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              isMobile ? "py-3 text-base" : ""
            }`}
          >
            <option value="">All Statuses</option>
            {Object.entries(OUTREACH_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Quick Filters - Digital Presence */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Digital Presence
          </label>
          <div className={`space-y-${isMobile ? "3" : "2"}`}>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.noWebsite || false}
                onChange={(e) =>
                  setFilters({ noWebsite: e.target.checked || undefined })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                No website
              </span>
            </label>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.noGoogleProfile || false}
                onChange={(e) =>
                  setFilters({ noGoogleProfile: e.target.checked || undefined })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                No Google Business Profile
              </span>
            </label>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.noPhone || false}
                onChange={(e) =>
                  setFilters({ noPhone: e.target.checked || undefined })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                No phone number
              </span>
            </label>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.noCompaniesHouse || false}
                onChange={(e) =>
                  setFilters({
                    noCompaniesHouse: e.target.checked || undefined,
                  })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                No Companies House data
              </span>
            </label>
          </div>
        </div>

        {/* Quick Filters - Business Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Status
          </label>
          <div className={`space-y-${isMobile ? "3" : "2"}`}>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.recentlyOpened || false}
                onChange={(e) =>
                  setFilters({ recentlyOpened: e.target.checked || undefined })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                Recently incorporated (12 months)
              </span>
            </label>
          </div>
        </div>

        {/* Quick Filters - Ratings & Reviews */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ratings & Reviews
          </label>
          <div className={`space-y-${isMobile ? "3" : "2"}`}>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.lowReviewVolume || false}
                onChange={(e) =>
                  setFilters({
                    lowReviewVolume: e.target.checked || undefined,
                  })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                Low review volume (&lt;10)
              </span>
            </label>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.poorGoogleRating || false}
                onChange={(e) =>
                  setFilters({
                    poorGoogleRating: e.target.checked || undefined,
                  })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                Poor Google rating (&lt;3 stars)
              </span>
            </label>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.lowFoodHygiene || false}
                onChange={(e) =>
                  setFilters({ lowFoodHygiene: e.target.checked || undefined })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                Low food hygiene (≤3)
              </span>
            </label>
          </div>
        </div>

        {/* Quick Filters - Outreach */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Outreach Activity
          </label>
          <div className={`space-y-${isMobile ? "3" : "2"}`}>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.contactedThisWeek || false}
                onChange={(e) =>
                  setFilters({
                    contactedThisWeek: e.target.checked || undefined,
                  })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                Contacted this week
              </span>
            </label>
            <label
              className={`flex items-center gap-3 ${isMobile ? "py-1" : ""}`}
            >
              <input
                type="checkbox"
                checked={filters.followUpDue || false}
                onChange={(e) =>
                  setFilters({ followUpDue: e.target.checked || undefined })
                }
                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  isMobile ? "w-5 h-5" : ""
                }`}
              />
              <span
                className={`text-gray-600 ${
                  isMobile ? "text-base" : "text-sm"
                }`}
              >
                Follow-up due
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Fixed footer section */}
      <div
        className={`${
          isMobile ? "" : "p-4 border-t border-gray-100 flex-shrink-0"
        }`}
      >
        {/* Results count */}
        <div
          className={`${isMobile ? "pt-4 border-t border-gray-200" : "mb-3"}`}
        >
          <p className={`text-gray-600 ${isMobile ? "text-base" : "text-sm"}`}>
            Showing{" "}
            <span className="font-medium text-gray-900">
              {pagination.total}
            </span>{" "}
            results
          </p>
        </div>

        {/* Create Plan Button */}
        <button
          onClick={onCreatePlan}
          className={`w-full py-2 px-4 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors ${
            isMobile ? "py-3 text-base mt-4" : ""
          }`}
        >
          Create Outreach Plan
        </button>
      </div>
    </div>
  );
}
